import ora from 'ora';
import { getImageDimensions } from '../../src/utils/imageUtils';
import * as config from '../commons/config';
import * as httpRequest from '../commons/httpRequest';
import * as testimServicesApi from '../commons/testimServicesApi';
import { getLogger } from '../commons/logger';
import { gridTypes } from '../commons/constants';
import { getSessionPlayer } from '../commons/getSessionPlayerRequire';
import { featureFlagsService } from '../commons/featureFlagsService';
import type { RunnerOptions } from '../runOptions';
import type { SerializedGrid } from './gridService';
import type { TestRunHandler } from 'src/testRunHandler';
import type {
    TvcCapabilitiesResponse,
    WebdriverAppiumSessionCapabilities,
} from 'clickim/src/background/session/MobileAgentSessionManager';
import type { RawMobileAppVersion } from 'services/src/commons/mongo/models/mobileAppVersion';
import type { TmaMobileDevice } from 'clickim/src/background/mobile/types';
import type { PlaybackMobileDevice } from 'services/src/grid/mobileGridService';

const logger = getLogger('mobile-grid-service');

const TDC_BASE_URL = 'https://tdc.tricentis-cloud.com';

interface BrowserStackSessionData {
    automation_session: {
        public_url: string;
    };
}

export interface TDCConnectionOptions {
    hostname: string;
    port: number;
    protocol: string;
    path: string;
}
interface GetAppInfoParams {
    options: RunnerOptions;
    testRunHandler: TestRunHandler;
    pendingAppUploads: Map<string, { isUploaded: boolean; uploadedAppId: string }>;
}

interface VerifyAppAlreadyUploadedParams extends Omit<GetAppInfoParams, 'options'> {
    projectId: string;
    gridId: string;
    gridType: string;
    mobileAppId: string;
}
interface AppPathGetterParams {
    testRunHandler: TestRunHandler;
    mobileApp?: RawMobileAppVersion;
    gridHost?: string;
}
interface GridsAppIdGetter {
    [key: string]: (appPathGetterParams: AppPathGetterParams) => string | undefined | null;
}

export interface Capabilities extends WebdriverAppiumSessionCapabilities {
    device?: string;
    mobile?: {
        browser: string;
        version: string; // browserstack: deviceName and version combined
    };
    os_version?: string; // TVC
    replayUrl?: string; // TVC
    deviceScreenSize: `${number}x${number}`; // TDC
    screen?: { scale?: number; width: number; height: number }; // TVC
    testobject_device_name?: string; // saucelabs device name
    testobject_test_report_url?: string; // saucelabs
}

interface GetDeviceNameFromSessionCapsParams {
    capabilities: Capabilities;
    gridType: string;
    projectId: string;
    projectType: string;
    companyId: string;
    gridId: string;
}
interface GetGridAppIdParams {
    gridData: SerializedGrid;
    testRunHandler: TestRunHandler;
    projectId: string;
}

export class MobileGridService {
    getLockDeviceApiUrl(gridInfo: SerializedGrid) {
        const apiBase = this.getBaseApiUrl(gridInfo);
        if (gridInfo.type === gridTypes.TESTIM_TDC) {
            return `${apiBase}/lock`;
        }
        return '';
    }

    getBaseApiUrl(gridInfo: SerializedGrid) {
        const { type: gridType, apiUrl } = gridInfo;

        if (gridType === gridTypes.TESTIM_TDC) {
            const baseApi = apiUrl || config.TDC_MOBILE_API_BASE_URL;
            return `${baseApi}/${config.TDC_DEVICES_API_PATH}/devices`;
        }
        return '';
    }

    getHeaders(gridInfo: SerializedGrid) {
        const headers = {
            ...(gridInfo.type === gridTypes.TESTIM_TDC && {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${gridInfo.accessToken!}`,
            }),
        };
        return headers;
    }

    buildRequestBody(gridInfo: SerializedGrid, deviceId: string) {
        const body = {
            // eslint-disable-next-line camelcase
            ...(gridInfo.type === gridTypes.TESTIM_TDC && { device_id: deviceId }),
        };
        return body;
    }

    async lockDevice(gridInfo: SerializedGrid, deviceId: string) {
        const url = this.getLockDeviceApiUrl(gridInfo);
        const headers = this.getHeaders(gridInfo);
        const body = this.buildRequestBody(gridInfo, deviceId);
        try {
            await httpRequest.post({ url, headers, body });
        } catch (error) {
            logger.debug(`error while locking device ${deviceId}`, { error });
            if (error.status === 401)
                throw new Error(`failed to lock device ${deviceId}, device may be already locked and in use`);
            throw error;
        }
    }

    async getTDCDeviceConnection(gridInfo: SerializedGrid, deviceId: string): Promise<TDCConnectionOptions> {
        const apiBase = this.getBaseApiUrl(gridInfo);
        const url = `${apiBase}/device_id:${deviceId}/automation-config`;
        try {
            const automationConfig = await httpRequest.get<{ [keys: string]: { driver_url: string } }>(
                url,
                undefined,
                this.getHeaders(gridInfo),
            );
            return this.extractTDCDeviceConnection(automationConfig, gridInfo.accessToken!);
        } catch (error) {
            logger.error('error while getting automation config for device', { error, deviceId });
            throw error;
        }
    }

    extractTDCDeviceConnection(automationConfig: any, token: string): TDCConnectionOptions {
        const firstKey = Object.keys(automationConfig)[0];
        const driverUrl = automationConfig[firstKey].driver_url;
        const url = new URL(driverUrl);
        return {
            hostname: url.hostname,
            port: Number(url.port),
            protocol: url.protocol.slice(0, -1),
            path: `/v0/${token}/wd/hub`,
        };
    }

    async getGridDirectConnection(gridInfo: SerializedGrid, deviceId: string): Promise<TDCConnectionOptions> {
        let connection = {} as TDCConnectionOptions;
        if (gridInfo.type === gridTypes.TESTIM_TDC) {
            connection = await this.getTDCDeviceConnection(gridInfo, deviceId);
        }
        return connection;
    }

    public getGridConnection(gridInfo: SerializedGrid) {
        if (gridInfo.type === gridTypes.TESTIM_TDC) {
            const path = gridInfo.host?.includes('ulb.tdc.tricentis-cloud.com')
                ? `/ulb/appium/v0/${gridInfo.accessToken}/wd/hub`
                : `/v0/${gridInfo.accessToken}/wd/hub`;
            return {
                protocol: gridInfo.protocol || 'https',
                hostname: gridInfo.host,
                port: gridInfo.port,
                path,
            };
        }
        if (gridInfo.type === gridTypes.TESTIM_TVC) {
            return {
                protocol: gridInfo.protocol || 'https',
                hostname: gridInfo.host,
                port: 443,
                path: '/wd/hub',
            };
        }

        const otherMobileGrids = [gridTypes.BROWSERSTACK, gridTypes.SAUCELABS] as string[];
        if (otherMobileGrids.includes(gridInfo.type!)) {
            return {
                protocol: gridInfo.protocol || 'https',
                hostname: gridInfo.host,
                port: 443, //default for mobile grids
                path: '/wd/hub',
                user: gridInfo.user,
                key: gridInfo.key,
            };
        }
        return undefined;
    }

    async getDeviceNameFromSessionCaps({
        capabilities,
        gridType,
        projectId,
        projectType,
        companyId,
        gridId,
    }: GetDeviceNameFromSessionCapsParams): Promise<string | undefined> {
        if (gridType === gridTypes.TESTIM_TDC) {
            const selectedDeviceArr = await testimServicesApi.getMobileDevicesFromGrid({
                projectId,
                projectType,
                companyId,
                gridId,
                selectors: `device_id:${capabilities.udid}`,
            });
            return selectedDeviceArr?.[0]?.name || capabilities.device;
        }
        if (gridType === gridTypes.BROWSERSTACK) {
            return capabilities.mobile?.version.split('-')[0] || capabilities.desired?.deviceName;
        }
        if (gridType === gridTypes.SAUCELABS) {
            return capabilities.testobject_device_name;
        }

        return capabilities.device;
    }

    public async updateDeviceOnRemoteTestResult(
        testRunHandler: TestRunHandler,
        activeSession: WebdriverIO.Browser,
        sessionUrl: string,
        options: RunnerOptions,
        isInEnhancedDomMode = false,
    ) {
        const { executionId, testId, testResultId } = testRunHandler;
        const {
            project: projectId,
            projectData: { type: projectType } = {},
            company: { companyId = '' } = {},
            gridData: { gridId = '', type: gridType = '' } = {},
        } = options;
        const capabilities = activeSession.capabilities as Capabilities;

        const osType = capabilities.platformName as 'ios' | 'android';
        let device: PlaybackMobileDevice;
        if (gridType === gridTypes.TESTIM_TVC) {
            const { getTvcDeviceInfoFromSessionCaps } = getSessionPlayer();
            device = getTvcDeviceInfoFromSessionCaps(
                activeSession.capabilities as TvcCapabilitiesResponse,
                isInEnhancedDomMode,
            );
        } else {
            device = await this.getDeviceFromAppiumCapabilities(
                capabilities,
                activeSession,
                sessionUrl,
                osType,
                gridType,
                projectId,
                projectType!,
                companyId,
                gridId,
                isInEnhancedDomMode,
            );
        }

        const remoteResultLink = await this.buildRemoteResultLink(
            options.gridData!,
            activeSession.sessionId,
            capabilities,
        );
        await testimServicesApi.updateTestStatus(projectId, executionId, testId, testResultId, 'RUNNING', {
            device,
            ...(remoteResultLink && { remoteResultLink }),
        });
        logger.info('device details updated on testResult', {
            testResultId,
            executionId,
            testId,
            device,
            remoteResultLink,
            gridType,
        });
        return device;
    }

    private getOsVersion(capabilities: Capabilities, gridType: string) {
        const osVersion: string | undefined = capabilities.platformVersion;
        if (!osVersion && gridType === gridTypes.BROWSERSTACK) {
            return capabilities.mobile?.version.split('-')[1];
        }

        return osVersion;
    }

    private async getScaleFactorAndSize(
        capabilities: Capabilities,
        sessionUrl: string,
        activeSession: WebdriverIO.Browser,
        osType: string,
        isInEnhancedDomMode: boolean,
    ) {
        if (capabilities.pixelRatio && typeof capabilities.deviceScreenSize === 'string') {
            const res = capabilities.deviceScreenSize.split('x');
            const width = Number(res[0]);
            const height = Number(res[1]);
            return { scaleFactor: capabilities.pixelRatio, width, height };
        }
        try {
            const { AppiumApi } = getSessionPlayer();
            const appiumApi = new AppiumApi(sessionUrl, activeSession.sessionId, null!, isInEnhancedDomMode);
            const [base64Screenshot, appiumSize] = await Promise.all([appiumApi.getScreenshot(), appiumApi.getDeviceSize()]);
            const { width, height } = await getImageDimensions(base64Screenshot);
            return { scaleFactor: width / appiumSize.width, width, height };
        } catch (err) {
            logger.warn('failed to get device scale factor from appium, applying default scale factor', { err });
            return { scaleFactor: osType === 'android' ? 1 : 2 };
        }
    }

    public async getDeviceFromAppiumCapabilities(
        capabilities: Capabilities,
        activeSession: WebdriverIO.Browser,
        sessionUrl: string,
        osType: 'android' | 'ios',
        gridType: string,
        projectId: string,
        projectType: string,
        companyId: string,
        gridId: string,
        isInEnhancedDomMode: boolean,
    ): Promise<PlaybackMobileDevice> {
        const [scaleFactorAndSize, deviceName] = await Promise.all([
            this.getScaleFactorAndSize(capabilities, sessionUrl, activeSession, osType, isInEnhancedDomMode),
            this.getDeviceNameFromSessionCaps({ capabilities, gridType, projectId, projectType, companyId, gridId }),
        ]);
        const osVersion = this.getOsVersion(capabilities, gridType);

        return {
            name: deviceName ?? '',
            model: capabilities.deviceModel,
            udid: capabilities.udid,
            osVersion,
            osType,
            //TODO:  improve this once we support virtual devices on Browserstack and SauceLabs
            isVirtual: false,
            ...scaleFactorAndSize,
        } as PlaybackMobileDevice;
    }

    private async buildRemoteResultLink(
        gridInfo: SerializedGrid,
        sessionId: string,
        caps: Capabilities | TvcCapabilitiesResponse,
    ) {
        if (gridInfo.type === gridTypes.BROWSERSTACK) {
            const sessionMetaData: BrowserStackSessionData = await this.fetchWithUserAndPasswordAuth({
                url: `${config.BROWSERSTACK_MOBILE_API_URL}/sessions/${sessionId}.json`,
                user: gridInfo.user!,
                key: gridInfo.key!,
            });
            return sessionMetaData.automation_session.public_url;
        }
        if (gridInfo.type === gridTypes.SAUCELABS) {
            // not public, not supported  https://docs.saucelabs.com/test-results/sharing-test-results/#:~:text=This%20solution%20is%20not%20supported%20for%20RDC.
            return (caps as Capabilities).testobject_test_report_url!;
        }
        if (gridInfo.type === gridTypes.TESTIM_TVC) {
            return caps.replayUrl!;
        }

        if (gridInfo.type === gridTypes.TESTIM_TDC) {
            return `${TDC_BASE_URL}/sessions/${sessionId}/waterfall`;
        }
        return '';
    }

    private async getTmaDeviceDetails(localTmaUrl: string, deviceId: string): Promise<PlaybackMobileDevice> {
        const tmaDevices = await httpRequest.get<TmaMobileDevice[]>(`${localTmaUrl}/api/devices`);
        const device = tmaDevices.find((d) => d.udid === deviceId);
        if (!device) {
            throw new Error(`device ${deviceId} not found`);
        }
        return {
            udid: device.udid,
            name: device.name,
            model: device.model,
            height: device.height,
            width: device.width,
            geo: 'local',
            osVersion: device.osVersion,
            osType: device.deviceType.toLowerCase() as 'android' | 'ios',
            scaleFactor: device.scaleFactor ?? 0,
            isVirtual: device.virtual ?? false,
            minApiVersion: device.minApiVersion,
        };
    }

    public async updateTmaDeviceDetails(deviceId: string, options: RunnerOptions, testRunHandler: TestRunHandler) {
        const { executionId, testId, testResultId } = testRunHandler;
        const { project: projectId, localTmaUrl } = options;
        const device = await this.getTmaDeviceDetails(localTmaUrl!, deviceId);
        await testimServicesApi.updateTestStatus(projectId, executionId, testId, testResultId, 'RUNNING', { device });
        logger.info('device details updated on testResult', { testResultId, executionId, testId, device });
        return device;
    }

    gridsAppIdGetter: GridsAppIdGetter = {
        [gridTypes.TESTIM_TDC]: ({ testRunHandler, mobileApp }) =>
            testRunHandler.getAppPath('testimTdcAppId', mobileApp),
        [gridTypes.TESTIM_TVC]: ({ testRunHandler, mobileApp }) =>
            testRunHandler.getAppPath('testimTvcAppId', mobileApp),
        [gridTypes.BROWSERSTACK]: ({ testRunHandler, mobileApp }) =>
            testRunHandler.getAppPath('browserStackAppId', mobileApp),
        [gridTypes.SAUCELABS]: ({ testRunHandler, mobileApp, gridHost }) =>
            testRunHandler.getSauceLabsAppPath(gridHost!, mobileApp),
    };

    async getGridAppId({ gridData, testRunHandler, projectId }: GetGridAppIdParams) {
        const gridType = gridData.type!;
        const mobileApp = testRunHandler.mobileApp as RawMobileAppVersion;

        const gridAppId = this.gridsAppIdGetter[gridType]({ testRunHandler, mobileApp, gridHost: gridData.host });
        // TODO:[2024-04-20] remove this if block. This is a temporary fix for the gridAppId structure (auto fix)
        if (gridAppId) {
            const allowFixGridAppIDStructure = featureFlagsService.flags.allowFixingAppIdStructure.isEnabled();
            if (allowFixGridAppIDStructure) {
                logger.info('fixing gridAppId structure', {
                    gridAppId,
                    projectId,
                    testResultId: testRunHandler.testResultId,
                    executionId: testRunHandler.executionId,
                });
                await testimServicesApi.fixGridAppIDStructure({
                    gridAppId,
                    gridId: gridData.gridId!,
                    projectId,
                    gridType,
                    appId: mobileApp.appId,
                    gridHost: gridData.host!,
                });
            }
            return gridAppId;
        }
        // end of TODO
        return testRunHandler.getAppPathByGridType(gridType, gridData.gridId!, mobileApp);
    }

    async getMobileAppDataByAppId({ appId, projectId, token }: { appId: string; projectId: string; token: string }) {
        const mobileApp = await testimServicesApi.getAppDetails({ appId, projectId });
        return { appUrl: `${config.SERVICES_HOST()}/storage${mobileApp.filePath}?access_token=${token}`, mobileApp };
    }

    calculateUploadRequestTimeOut(size: number) {
        let dynamicTimeout = 0;
        const fileSizeInKB = Math.ceil(size / 1024);
        const uploadTimeInMinutes = Math.ceil(fileSizeInKB / (config.MINIMUM_KNOWN_UPLOAD_SPEED_PER_SECONDS * 60));
        dynamicTimeout = (uploadTimeInMinutes + 1) * 60 * 1000;

        return Math.max(config.APP_UPLOAD_TIMEOUT, dynamicTimeout);
    }

    async uploadAppToGrid({
        projectId,
        testRunHandler,
        mobileApp,
        gridId,
        gridType,
    }: {
        projectId: string;
        testRunHandler: TestRunHandler;
        mobileApp?: RawMobileAppVersion;
        gridId: string;
        gridType: string;
    }) {
        const gridDic: { [key: string]: { name: string } } = {
            [gridTypes.BROWSERSTACK]: { name: 'Browserstack' },
            [gridTypes.TESTIM_TVC]: { name: 'Mobile Virtual Grid' },
        };

        const gridName = gridDic[gridType].name;
        if (testRunHandler.isAppFromDevice) {
            throw new Error(
                `Application from your device, ${gridName} grid dose not support application from device. Please choose a compatible application for testing.`,
            );
        }
        const app = mobileApp || (testRunHandler.mobileApp as RawMobileAppVersion);
        const spinner = ora(`uploading app to ${gridName} please wait..`).start();
        try {
            logger.info(`uploading app to ${gridName} during test run`, {
                mobileApp,
                projectId,
                testResultId: testRunHandler.testResultId,
                executionId: testRunHandler.executionId,
            });
            const result = await testimServicesApi.uploadAppToGrid({
                projectId,
                gridId,
                app,
                timeout: this.calculateUploadRequestTimeOut(app.fileSize!),
            });
            spinner.succeed(`app uploaded successfully to ${gridName}`);
            return result.gridAppId;
        } catch (error) {
            spinner.fail(`failed to upload app to ${gridName}`);
            logger.error(`failed to upload app to ${gridName}`, {
                error,
                mobileApp,
                projectId,
                testResultId: testRunHandler.testResultId,
                executionId: testRunHandler.executionId,
            });
            throw new Error(`failed to upload app to ${gridName}`);
        }
    }

    async verifyAppAlreadyUploaded({
        testRunHandler,
        pendingAppUploads,
        projectId,
        gridId,
        gridType,
        mobileAppId,
    }: VerifyAppAlreadyUploadedParams) {
        const appUploadStatus = pendingAppUploads.get(mobileAppId);
        if (appUploadStatus?.isUploaded) {
            return appUploadStatus.uploadedAppId;
        }
        const appPath = await this.uploadAppToGrid({ projectId, testRunHandler, gridId, gridType });
        pendingAppUploads.set(mobileAppId, { isUploaded: true, uploadedAppId: appPath });
        return appPath;
    }

    async getAppIdFromGridOrTestimPublicLink({
        testRunHandler,
        options,
        pendingAppUploads,
    }: GetAppInfoParams): Promise<string> {
        let appPath = '';
        let overriddenMobileApp;
        const { executionId, testResultId } = testRunHandler;
        const { appId = '', project: projectId, authData: { token = '' } = {}, gridData = {} } = options;
        const { gridId = '', type: gridType = '' } = gridData;

        if (!appId && testRunHandler.isAppFromDevice) {
            return appPath;
        }

        if (appId) {
            logger.info('getting appData by appId', { appId, projectId, testResultId, executionId });
            const appData = await this.getMobileAppDataByAppId({ appId, projectId, token });
            appPath = appData.appUrl;
            overriddenMobileApp = appData.mobileApp;
            testRunHandler.nativeApp = overriddenMobileApp;
        } else {
            logger.info('no appId setting appPath from app associated with the test', {
                projectId,
                testResultId,
                executionId,
            });
            appPath = testRunHandler.downloadableAppPublicLink || '';
        }

        const mobileAppId = appId || (testRunHandler.mobileApp as RawMobileAppVersion).appId;

        const gridAppId = await this.getGridAppId({ projectId, gridData, testRunHandler });
        const isReUploadAppNeeded =
            gridType === gridTypes.BROWSERSTACK || gridType === gridTypes.TESTIM_TVC;

        if (gridAppId) {
            const projectType = options.projectData!.type! as 'ios' | 'android';
            const isAppExistsOnGrid = await this.verifyAppStillExistsOnGrid({ gridAppId, gridData, projectType });

            if (isAppExistsOnGrid) {
                logger.info('app exists on grid, using gridAppId', {
                    gridAppId,
                    projectId,
                    testResultId,
                    executionId,
                    mobileAppId,
                });
                appPath = gridAppId;
            }
            if (!isAppExistsOnGrid && isReUploadAppNeeded) {
                logger.info('app not exists on grid, uploading it', {
                    gridAppId,
                    projectId,
                    testResultId,
                    executionId,
                    mobileAppId,
                });
                appPath = await this.verifyAppAlreadyUploaded({
                    testRunHandler,
                    pendingAppUploads,
                    projectId,
                    gridId,
                    gridType,
                    mobileAppId,
                });
            }
            if (!isAppExistsOnGrid && !isReUploadAppNeeded) {
                logger.info('app not exists on grid, using testimStorage url', {
                    projectId,
                    testResultId,
                    executionId,
                });
            }
        }

        if (!gridAppId && isReUploadAppNeeded) {
            appPath = await this.verifyAppAlreadyUploaded({
                testRunHandler,
                pendingAppUploads,
                projectId,
                gridId,
                gridType,
                mobileAppId,
            });
        }
        return appPath;
    }

    async verifyAppStillExistsOnGrid({
        gridAppId,
        gridData,
        projectType,
    }: {
        gridAppId: string;
        gridData: SerializedGrid;
        projectType: 'ios' | 'android';
    }) {
        const { accessToken, user, host, key, type, apiUrl } = gridData;

        if (type === gridTypes.BROWSERSTACK) {
            const url = `${config.BROWSERSTACK_MOBILE_API_URL}/recent_apps`;
            const response = await this.fetchWithUserAndPasswordAuth<{ app_url: string }[]>({
                url,
                user: user!,
                key: key!,
            });
            if (Array.isArray(response)) {
                return response.some((app) => app.app_url === gridAppId);
            }
            return false;
        }

        if (type === gridTypes.SAUCELABS) {
            const url = `https://${host?.replace('ondemand', 'api')}/v1/storage/files`;
            const response = await this.fetchWithUserAndPasswordAuth<{ items: { id: string }[] }>({
                url,
                user: user!,
                key: key!,
            });
            return response.items.some((app) => app.id === gridAppId);
        }

        if (type === gridTypes.TESTIM_TDC) {
            let url = `${apiUrl}/${config.TDC_APPS_API_PATH}/apps`;
            if (!apiUrl) {
                url = `${config.TDC_MOBILE_API_BASE_URL}/${config.TDC_APPS_API_PATH}/apps`;
            }
            const response = await this.fetchWithTokenAuth<{ apps: { app_id: string }[] }>({
                url,
                accessToken: accessToken!,
                authType: 'Bearer',
            });
            return response.apps.some((app: { app_id: string }) => app.app_id === gridAppId);
        }

        if (type === gridTypes.TESTIM_TVC) {
            const url = `${config.TVC_MOBILE_API_URL}/versions/${gridAppId}/info`;
            const token = gridData[`${projectType}Token`];
            try {
                await this.fetchWithTokenAuth<{ id: string }[]>({
                    url,
                    accessToken: token!,
                    authType: 'Upload-Token',
                    userAgent: 'Testim-CLI',
                });
                return true;
            } catch (error) {
                if (error.status !== 404) {
                    logger.error('error while verifying app exists on TVC', { error });
                }
                return false;
            }
        }

        throw new Error(`grid type ${type} is not supported`);
    }

    async fetchWithUserAndPasswordAuth<T>({ url, user, key }: { url: string; user: string; key: string }) {
        const headers = { Authorization: `Basic ${Buffer.from(`${user}:${key}`).toString('base64')}` };
        return await httpRequest.get<T>(url, undefined, headers);
    }

    async fetchWithTokenAuth<T>({
        url,
        accessToken,
        authType,
        userAgent,
    }: {
        url: string;
        accessToken: string;
        authType: string;
        userAgent?: string;
    }) {
        const headers = {
            Authorization: `${authType} ${accessToken}`,
            ...(userAgent && { 'User-Agent': userAgent }),
        };
        return await httpRequest.get<T>(url, undefined, headers);
    }
}

export const mobileGridService = new MobileGridService();
